// ============================================================================
// fedChat.ts — «Написать по номеру HydraID» (межбазовый чат через Хаб, 1b-2).
//
// Сервер сам решает, где получатель: номер ЭТОЙ базы → обычный direct-чат,
// номер другой базы → адресная книга Центра и контакт-сущность (SSoT —
// FedChatService.StartChatByHydraId). Клиент не угадывает и не создаёт чат
// вручную — открывает ТОЛЬКО тот диалог, который вернул сервер (общий путь
// навигации — lib/chatNavigation.openConversation).
//
// Ошибки сервера переводятся в понятные человеку тексты (fail-closed политика
// межбазового чата — настройка базы «Федерация → Чат»); техническая деталь
// уходит в журнал регистрации.
// ============================================================================

import { openConversation } from '@/lib/chatNavigation';
import { errorCode, friendlyError, logErrorToJournal } from '@/lib/errors';
import { normalize } from '@/lib/hydraid';
import { getSDK } from '@/store/sdkStore';

// Коды ConnectRPC (connect-es — числа, connect-go в тексте — имена).
const CODE_INVALID_ARGUMENT = 3;
const CODE_NOT_FOUND = 5;
const CODE_FAILED_PRECONDITION = 9;

function hasCode(e: unknown, numeric: number, name: string): boolean {
  const code = errorCode(e);
  if (typeof code === 'number') return code === numeric;
  if (typeof code === 'string') {
    return code.trim().toLowerCase().replace(/[\s_-]/g, '') === name;
  }
  return false;
}

/** Человеческий текст ошибки открытия межбазового чата. */
export function fedChatErrorMessage(e: unknown): string {
  if (hasCode(e, CODE_FAILED_PRECONDITION, 'failedprecondition')) {
    return 'Межбазовый чат выключен в настройках базы (Федерация → Чат)';
  }
  if (hasCode(e, CODE_INVALID_ARGUMENT, 'invalidargument')) {
    return 'Неверный номер';
  }
  if (hasCode(e, CODE_NOT_FOUND, 'notfound')) {
    return 'Получатель не найден';
  }
  return friendlyError(e);
}

/**
 * Открыть (создать/найти) личный чат по номеру HydraID и перейти в него.
 * Бросает Error с текстом для пользователя; техническая причина — в журнале.
 */
export async function openFedChatByHydraId(hydraId: string): Promise<void> {
  const number = normalize(hydraId);
  try {
    const res = await getSDK().fedChat.startChatByHydraId({ hydraId: number });
    const conversationId = res.conversationId.trim();
    if (!conversationId) {
      throw new Error('сервер не вернул созданный диалог');
    }
    openConversation(conversationId);
  } catch (e) {
    logErrorToJournal(e, `межбазовый чат: написать по номеру ${number || hydraId}`);
    throw new Error(fedChatErrorMessage(e), { cause: e });
  }
}
